import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileWord,
  faLayerGroup,
  faPenToSquare,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"

interface EditorStartScreenProps {
  /** Opens the Word conversion (File > Convert Word questionnaire…) */
  onUploadWord: () => void
  /** Closes the start screen and puts the cursor in the editor */
  onBlank: () => void
  /** Describe it to AI; greyed out ("Coming soon") until given */
  onDescribe?: () => void
  /** Start from a template; greyed out ("Coming soon") until given */
  onTemplate?: () => void
}

interface StartTileProps {
  title: string
  text: string
  icon: IconDefinition
  iconClassName: string
  onClick?: () => void
}

/**
 * One way to start. Without a handler it is greyed out and says "Coming soon".
 *
 * @param props - The props for the component
 * @returns The tile
 */
const StartTile: React.FC<StartTileProps> = ({
  title,
  text,
  icon,
  iconClassName,
  onClick,
}) => {
  const enabled = Boolean(onClick)
  return (
    <button
      type="button"
      onClick={enabled ? onClick : undefined}
      aria-disabled={!enabled}
      className={`relative flex flex-col items-center text-center gap-2 rounded-xl border bg-white px-4 py-5 transition ${
        enabled
          ? "border-gray-200 hover:border-[var(--dq-brand)] hover:shadow-[0_0_0_3px_var(--dq-brand-soft)] cursor-pointer"
          : "border-gray-100 opacity-55 cursor-not-allowed"
      }`}
    >
      {!enabled && (
        <span className="absolute top-2 right-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
          Coming soon
        </span>
      )}
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-xl text-xl ${iconClassName}`}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="text-sm font-semibold text-gray-900">{title}</span>
      <span className="text-xs leading-snug text-gray-500">{text}</span>
    </button>
  )
}

/**
 * The empty editor's start screen: four ways in (describe it to AI, a
 * template, a Word document, or blank). It sits over the empty editor; the
 * Editor decides when it shows (useStartScreen).
 *
 * @param props - The props for the component
 * @returns The start screen
 */
const EditorStartScreen: React.FC<EditorStartScreenProps> = ({
  onUploadWord,
  onBlank,
  onDescribe,
  onTemplate,
}) => (
  <div
    className="absolute inset-0 z-40 flex items-center justify-center overflow-auto bg-white p-6"
    // The editor area underneath handles clicks and right-clicks on its text.
    onClick={(e) => e.stopPropagation()}
    onContextMenu={(e) => e.stopPropagation()}
  >
    <div className="w-full max-w-3xl text-center">
      <h2 className="mb-1 text-xl font-semibold text-gray-900">
        Start your questionnaire
      </h2>
      <p className="mb-6 text-sm text-gray-500">
        Pick a way in. You can switch to any of these later.
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StartTile
          title="Describe it"
          text="Tell AI what the survey is about and get a draft"
          icon={faWandMagicSparkles}
          iconClassName="bg-gradient-to-br from-[#0ea5c6] to-indigo-500 text-white"
          onClick={onDescribe}
        />
        <StartTile
          title="Template"
          text="Start from a ready-made questionnaire"
          icon={faLayerGroup}
          iconClassName="bg-gray-100 text-gray-600"
          onClick={onTemplate}
        />
        <StartTile
          title="Upload Word"
          text="Convert an existing .docx questionnaire"
          icon={faFileWord}
          iconClassName="bg-[#2b579a] text-white"
          onClick={onUploadWord}
        />
        <StartTile
          title="Blank"
          text="Start typing from scratch"
          icon={faPenToSquare}
          iconClassName="border-2 border-dashed border-gray-300 text-gray-400"
          onClick={onBlank}
        />
      </div>
    </div>
  </div>
)

export default EditorStartScreen
